import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Gamepad2, AlertTriangle, Check } from "lucide-react";
import { useT } from "../context/LanguageContext";

export default function ClassicsDisputesTab() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/classics/disputes");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const open = (m) => {
    setSelected(m);
    setHomeScore(m.requester_submission?.my_score ?? 0);
    setAwayScore(m.receiver_submission?.my_score ?? 0);
    setNote("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/classics/matches/${selected.id}/override`, {
        home_score: parseInt(homeScore, 10) || 0,
        away_score: parseInt(awayScore, 10) || 0,
        note: note.trim() || undefined,
      });
      toast.success(t("clAdm.resolvedToast"));
      setSelected(null);
      await refresh();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <TactLoader label={t("clAdm.loading")} />;

  return (
    <div className="space-y-6" data-testid="cl-admin-disputes">
      <SectionTitle kicker={`// ${t("clAdm.kicker")}`}>{t("clAdm.title")}</SectionTitle>
      {rows.length === 0 ? (
        <Panel testId="cl-admin-disputes-empty">
          <div className="font-mono text-sm text-[#71717A]">[ {t("clAdm.empty")} ]</div>
        </Panel>
      ) : (
        <ul className="space-y-3">
          {rows.map((m) => (
            <li key={m.id} className="border border-[#E11D48] p-4 space-y-3" data-testid={`cl-admin-dispute-${m.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48] flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {m.game?.name?.toUpperCase()} · DISPUTE
                  </div>
                  <div className="font-heading text-xl uppercase tracking-wider mt-1">
                    <Link to={`/players/${m.requester?.username}`} className="hover:text-[#7C3AED] transition-colors">{m.requester?.username}</Link>
                    {" "}<span className="text-[#71717A]">vs</span>{" "}
                    <Link to={`/players/${m.receiver?.username}`} className="hover:text-[#7C3AED] transition-colors">{m.receiver?.username}</Link>
                  </div>
                  <div className="font-mono text-[10px] text-[#71717A] mt-1">{m.scheduled_at}</div>
                </div>
                <button
                  type="button"
                  onClick={() => open(m)}
                  data-testid={`cl-admin-resolve-${m.id}`}
                  className="inline-flex items-center gap-1 bg-[#E11D48] text-white hover:bg-[#BE123C] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors"
                >
                  <Gamepad2 className="w-3 h-3" /> {t("clAdm.resolve")}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{m.requester?.username} {t("clAdm.reported")}</div>
                  <div className="font-heading text-2xl mt-1">
                    {m.requester_submission ? `${m.requester_submission.my_score} - ${m.requester_submission.opponent_score}` : <span className="text-[#71717A] text-base">{t("clAdm.notSubmitted")}</span>}
                  </div>
                </div>
                <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{m.receiver?.username} {t("clAdm.reported")}</div>
                  <div className="font-heading text-2xl mt-1">
                    {m.receiver_submission ? `${m.receiver_submission.opponent_score} - ${m.receiver_submission.my_score}` : <span className="text-[#71717A] text-base">{t("clAdm.notSubmitted")}</span>}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="cl-admin-resolve-modal" onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="w-full max-w-md bg-white dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] p-6 space-y-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">{selected.game?.name?.toUpperCase()}</div>
              <h3 className="font-heading text-2xl uppercase tracking-wider">{t("clAdm.modalTitle")}</h3>
            </div>
            <p className="font-body text-xs text-[#71717A]">{t("clAdm.modalHelp")}</p>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{selected.requester?.username}</label>
                  <input type="number" min="0" max="99" value={homeScore} onChange={(e) => setHomeScore(e.target.value)} required data-testid="cl-admin-home-score" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-2xl font-heading focus:border-[#7C3AED] outline-none text-center text-[#09090B] dark:text-[#F4F4F5]" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{selected.receiver?.username}</label>
                  <input type="number" min="0" max="99" value={awayScore} onChange={(e) => setAwayScore(e.target.value)} required data-testid="cl-admin-away-score" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-2xl font-heading focus:border-[#7C3AED] outline-none text-center text-[#09090B] dark:text-[#F4F4F5]" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("clAdm.note")}</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} data-testid="cl-admin-note" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]" />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setSelected(null)} className="border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">{t("clAdm.cancel")}</button>
                <button type="submit" disabled={busy} data-testid="cl-admin-confirm" className="inline-flex items-center gap-1 bg-[#E11D48] text-white hover:bg-[#BE123C] disabled:opacity-40 font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
                  <Check className="w-3 h-3" /> {busy ? t("clAdm.confirming") : t("clAdm.confirm")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
